import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Text } from '@/components/ui/text';
import { useBulkDeleteUsersService } from '@/services/backoffice';
import type { BackOfficeUserProfile } from '@ditto/shared';
import { type FC } from 'react';

interface BulkDeleteUsersModalProps {
  users: BackOfficeUserProfile[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const BulkDeleteUsersModal: FC<BulkDeleteUsersModalProps> = ({
  users,
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { bulkDeleteUsers, isPending } = useBulkDeleteUsersService();

  const handleDelete = () => {
    const ids = users.map(u => u.id);
    bulkDeleteUsers(ids, {
      onSuccess: () => {
        onOpenChange(false);
        onSuccess?.();
      },
      onError: error => {
        console.error('Failed to delete users:', error);
      },
    });
  };

  const count = users.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Delete Users</DialogTitle>
          <DialogDescription>
            This action cannot be undone. Users will be permanently deleted and
            removed from all workspaces.
          </DialogDescription>
        </DialogHeader>
        <Text>
          {`Are you sure you want to delete ${count} user${count === 1 ? '' : 's'}?`}
        </Text>
        {count <= 5 && (
          <ul className="list-disc pl-5 text-sm text-muted-foreground">
            {users.map(u => (
              <li key={u.id}>
                {u.firstName} {u.lastName} ({u.email})
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending}
          >
            {isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
